import { useEffect, useRef, useState } from 'react';

import type { MediaRef, MediaDetails, MediaType, LibraryCollection, TMDBSeason } from '../types';
import { LANGUAGE, TMDB_API_BASE_URL } from '../constants';
import { STREAMS } from '../constants/streams';

import { LibraryActions } from './LibraryActions';
import { useConfig } from '../context/ConfigContext';
import { LoadingSpinner } from './LoadingSpinner';

import styles from './DetailsModal.module.css';

interface DetailsModalProps {
  itemId: number;
  mediaType: MediaType;
  onClose: () => void;
  currentCollection?: LibraryCollection;
  updateCollection: (ref: MediaRef, target: LibraryCollection) => void;
}

export function DetailsModal({
  itemId,
  mediaType,
  onClose,
  currentCollection,
  updateCollection
}: DetailsModalProps) {
  const { tmdbApiKey } = useConfig();

  const [details, setDetails] = useState<MediaDetails | any | null>(null);
  const [loading, setLoading] = useState(true);

  const [selectedSeason, setSelectedSeason] = useState<number | null>(null);
  const [episodes, setEpisodes] = useState<any[]>([]);
  const [loadingEpisodes, setLoadingEpisodes] = useState(false);

  const [showSeasonsLeft, setShowSeasonsLeft] = useState(false);
  const [showSeasonsRight, setShowSeasonsRight] = useState(false);
  const [showEpisodesLeft, setShowEpisodesLeft] = useState(false);
  const [showEpisodesRight, setShowEpisodesRight] = useState(false);
  const seasonsRef = useRef<HTMLDivElement | null>(null);
  const episodesRef = useRef<HTMLDivElement | null>(null);
  const episodeScrollToRef = useRef<HTMLDivElement | null>(null);
  const [isClosingEpisodes, setIsClosingEpisodes] = useState(false);

  // --- FUNCTIONS ---
  const handleSeasonClick = async (seasonNumber: number) => {
    if (selectedSeason === seasonNumber) {
      setIsClosingEpisodes(true);

      setTimeout(() => {
        setSelectedSeason(null);
        setEpisodes([]);
        setIsClosingEpisodes(false);
      }, 400);
      return;
    }

    setSelectedSeason(seasonNumber);
    setIsClosingEpisodes(false);
    setLoadingEpisodes(true);

    try {
      const response = await fetch(
        `${TMDB_API_BASE_URL}/tv/${details.id}/season/${seasonNumber}?api_key=${tmdbApiKey}&language=${LANGUAGE}`
      );
      const seasonData = await response.json();
      setEpisodes(seasonData.episodes || []);
    } catch (err) {
      console.error("Error fetching episodes:", err);
    } finally {
      setLoadingEpisodes(false);
    }
  };

  const scroll = (ref: React.RefObject<HTMLDivElement | null>, direction: 'left' | 'right', amount: number = 550) => {
    if (ref.current) {
      // const scrollSize = padding + (elementWidth + gap/2) * scrolledItemsCount;
      const scrollAmount = direction === 'left' ? -(amount) : (amount);
      ref.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const updateButtonVisibility = (ref: React.RefObject<HTMLDivElement | null>, setLeft: (v: boolean) => void, setRight: (v: boolean) => void) => {
    if (ref.current) {
      const { scrollLeft, scrollWidth, clientWidth } = ref.current;
      setLeft(scrollLeft > 10);
      // Show right if we haven't reached the end (with a 5px buffer)
      setRight(scrollLeft + clientWidth < scrollWidth - 5);
    }
  };

  const getDirector = () => {
    if (mediaType === 'movie') {
      return details.credits?.crew?.find((person: any) => person.job === 'Director')?.name;
    } else {
      // For TV Shows, "created_by" is usually the most relevant "Director" equivalent
      return details.created_by?.[0]?.name || details.credits?.crew?.find((person: any) => person.job === 'Executive Producer')?.name;
    }
  };

  const isReleased = (dateString: string | null) => {
    if (!dateString) return false;
    const releaseDate = new Date(dateString);
    const today = new Date();
    return releaseDate <= today;
  };

  const formatRuntime = (minutes: number) => {
    if (minutes < 60) return `${minutes} min`;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m > 0 ? `${h}h ${m}m` : `${h}h`;
  };

  const formatDate = (dateString: string, includeDay = true) => {
    if (!dateString) return 'Date inconnue';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: includeDay ? 'numeric' : undefined,
      month: includeDay ? 'short' : undefined,
      year: 'numeric'
    });
  };

  // --- EFFECTS ---

  // Fetch item details on modal open
  useEffect(() => {
    async function fetchFullDetails() {
      const url = `${TMDB_API_BASE_URL}/${mediaType}/${itemId}?api_key=${tmdbApiKey}&language=${LANGUAGE}&append_to_response=watch/providers,external_ids,credits`;
      try {
        const response = await fetch(url);
        const data = await response.json();
        setDetails(data);
      } catch (err) {
        console.error("Erreur détails:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchFullDetails();
  }, [itemId, mediaType]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Use ESC to close modal
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEsc);

    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = originalStyle;
    };
  }, [onClose]);

  /* Season click */
  useEffect(() => {
    const seasonsEl = seasonsRef.current;
    const episodesEl = episodesRef.current;

    const onScrollSeasons = () => {
      updateButtonVisibility(seasonsRef, setShowSeasonsLeft, setShowSeasonsRight);
    }

    const onScrollEpisodes = () => {
      updateButtonVisibility(episodesRef, setShowEpisodesLeft, setShowEpisodesRight);
    };

    seasonsEl?.addEventListener('scroll', onScrollSeasons);
    episodesEl?.addEventListener('scroll', onScrollEpisodes);

    const timeout = setTimeout(() => {
      onScrollSeasons();
      onScrollEpisodes();
    }, 100);

    return () => {
      seasonsEl?.removeEventListener('scroll', onScrollSeasons);
      episodesEl?.removeEventListener('scroll', onScrollEpisodes);
      clearTimeout(timeout);
    };
  }, [details, episodes]);

  /* On selected season change scroll to episodes list */
  useEffect(() => {
    if (selectedSeason !== null && !loadingEpisodes && episodeScrollToRef.current) {
      const timer = setTimeout(() => {
        episodeScrollToRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 10);

      return () => clearTimeout(timer);
    }
  }, [selectedSeason, loadingEpisodes]);

  // --- RENDER ---

  if (loading) return (
    <div className={styles.overlay}>
      <LoadingSpinner size='lg' fullPage />
    </div>
  );

  if (!details) return null;

  // Handling differences between Movie and TV
  const title = details.title || details.name;
  const searchTerm = encodeURIComponent(title);
  const rawDate = details.release_date || details.first_air_date;
  const formattedDate = rawDate
    ? new Date(rawDate).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
    : 'Date inconnue';

  const runtime = details.runtime || (details.episode_run_time && details.episode_run_time.length > 0
    ? details.episode_run_time[0]
    : null);
  const providers = details["watch/providers"]?.results?.FR?.flatrate || [];

  const imdbId = details.imdb_id || details.external_ids?.imdb_id;
  const imdbUrl = imdbId ? `https://www.imdb.com/title/${imdbId}` : null;

  const directorName = getDirector();

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.floatingSidebar} onClick={(e) => e.stopPropagation()}>
        <LibraryActions
          itemId={itemId}
          mediaType={mediaType}
          variant="modal"
          currentCollection={currentCollection}
          updateCollection={updateCollection}
        />
      </div>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Top Controls Container */}
        <div className={styles.topControls}>
          {/* Close button */}
          <button className={styles.actionBtn} onClick={onClose}>
            <span>✕</span>
          </button>
        </div>

        {/* Card hero section */}
        <div className={styles.hero}>
          <img
            src={details.backdrop_path
              ? `https://image.tmdb.org/t/p/w1280${details.backdrop_path}`
              : '/assets/images/placeholders/media.svg'}
            alt=""
            className={styles.backdrop}
          />
          <div className={styles.heroOverlay}>
            <div className={styles.badge}>{mediaType === 'movie' ? 'Film' : 'Série'}</div>
            <h2>{title}</h2>
            <p className={styles.meta}>
              {formattedDate} {runtime ? `• ${runtime} min` : ''} • ★ {details.vote_average.toFixed(1)}
              {imdbUrl && (
                <a href={imdbUrl} className={styles.imdbLink} target="_blank" rel="noopener noreferrer">
                  <img
                    src="https://upload.wikimedia.org/wikipedia/commons/6/69/IMDB_Logo_2016.svg"
                    alt="IMDb"
                    style={{ width: 'auto', height: '27px', borderRadius: '4px' }}
                  />
                </a>
              )}
            </p>
            <div className={styles.genres}>
              {details.genres?.map((g: any) => (
                <span key={g.id} className={styles.genreTag}>{g.name}</span>
              ))}
            </div>
          </div>
        </div>

        {/* Card details section */}
        <div className={styles.body}>
          <div className={styles.mobileActions}>
            <LibraryActions
              itemId={itemId}
              mediaType={mediaType}
              variant="modal"
              currentCollection={currentCollection}
              updateCollection={updateCollection}
            />
          </div>

          {/* Seasons */}
          {mediaType === 'tv' && (
            <section className={styles.section} ref={episodeScrollToRef}>
              <h3>Saisons</h3>
              <div className={styles.scrollWrapper}>
                {/* Left scroll */}
                {(showSeasonsLeft) &&
                  <button className={`${styles.navBtn} ${styles.left}`} onClick={() => scroll(seasonsRef, 'left')}>
                    ‹
                  </button>
                }

                <div
                  className={`${styles.seasonsGrid} ${selectedSeason !== null ? styles.hasSelection : ''}`}
                  ref={seasonsRef}
                >
                  {details.seasons?.map((season: TMDBSeason) => {
                    const aired = isReleased(season.air_date);
                    const isNew = season.air_date &&
                      (new Date().getTime() - new Date(season.air_date).getTime()) < 7 * 24 * 60 * 60 * 1000 && aired;

                    return (
                      <div
                        key={season.id}
                        className={`${styles.seasonCard} ${selectedSeason === season.season_number ? styles.activeSeason : ''} ${!aired ? styles.notAired : ''}`}
                        onClick={() => handleSeasonClick(season.season_number)}
                      >
                        <div className={styles.seasonPosterWrapper}>
                          {season.poster_path ? (
                            <img src={`https://image.tmdb.org/t/p/w200${season.poster_path}`} alt={season.name} />
                          ) : (
                            <div className={styles.posterPlaceholder}><span>{season.season_number}</span></div>
                          )}
                          {isNew && <div className={styles.newBadge}>NOUVEAU</div>}
                        </div>
                        <div className={styles.seasonInfo}>
                          <span className={styles.seasonName}>{season.name}</span>
                          <span className={styles.episodeCount}>
                            {!aired ? "À venir" : `${formatDate(season.air_date, false)} • ${season.episode_count} Épisodes`}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Right scroll */}
                {(showSeasonsRight) &&
                  <button className={`${styles.navBtn} ${styles.right}`} onClick={() => scroll(seasonsRef, 'right')}>
                    ›
                  </button>
                }
              </div>
            </section>
          )}

          {/* Episodes list */}
          {selectedSeason !== null && (
            <section
              className={`${styles.section} ${isClosingEpisodes ? styles.episodeSectionExit : styles.episodeSectionEnter}`}
            >
              <div className={styles.episodeHeader}>
                <h3>Épisodes - Saison {selectedSeason}</h3>
              </div>

              {loadingEpisodes ? (
                <div className={styles.episodeLoader}><LoadingSpinner size="sm" /></div>
              ) : (
                <div className={styles.scrollWrapper}>
                  {/* Left scroll */}
                  {(showEpisodesLeft) &&
                    <button className={`${styles.navBtn} ${styles.left}`} onClick={() => scroll(episodesRef, 'left')}>
                      ‹
                    </button>
                  }

                  <div className={styles.episodeHorizontalScroll} ref={episodesRef}>
                    {episodes.map((ep, index) => {
                      const aired = isReleased(ep.air_date);
                      const isNew = ep.air_date &&
                        (new Date().getTime() - new Date(ep.air_date).getTime()) < 7 * 24 * 60 * 60 * 1000 && aired;

                      return (
                        <div
                          key={ep.id}
                          className={`${styles.episodeCard} ${!aired ? styles.notAired : ''}`}
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <div className={styles.epStillWrapper}>
                            <img src={ep.still_path ? `https://image.tmdb.org/t/p/w300${ep.still_path}` : '/assets/images/placeholders/media.svg'} alt={ep.name} />
                            <div className={styles.epBadge}>E{ep.episode_number}</div>
                            {isNew && <div className={styles.newBadge}>NOUVEAU</div>}
                            {ep.runtime && aired && <div className={styles.epRuntimeBadge}>{formatRuntime(ep.runtime)}</div>}
                          </div>
                          <div className={styles.epInfo}>
                            <span className={styles.epTitle}>{ep.name}</span>
                            <span className={styles.epAirDate}>
                              {!aired ? formatDate(ep.air_date) : formatDate(ep.air_date)}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Right scroll */}
                  {(showEpisodesRight) &&
                    <button className={`${styles.navBtn} ${styles.right}`} onClick={() => scroll(episodesRef, 'right')}>
                      ›
                    </button>
                  }
                </div>
              )}
            </section>
          )}

          {/* Director */}
          {directorName && (
            <section className={styles.section}>
              <div className={styles.directorWrapper}>
                <span className={styles.metaLabel}>{mediaType === 'movie' ? 'Réalisateur ' : 'Créateur '}: </span>
                <span className={styles.directorName}>{directorName}</span>
              </div>
            </section>
          )}

          {/* Cast */}
          <section className={styles.section}>
            <h3>Casting</h3>
            {details.credits?.cast && details.credits.cast.length > 0 ? (
              <div className={styles.castRow}>
                {details.credits.cast.slice(0, 6).map((actor: any) => (
                  <div key={actor.id} className={styles.actorCard}>
                    <div className={styles.actorImageWrapper}>
                      <img
                        src={actor.profile_path
                          ? `https://image.tmdb.org/t/p/w185${actor.profile_path}`
                          : '/assets/images/placeholders/profile.svg'}
                        alt={actor.name}
                      />
                    </div>
                    <div className={styles.actorInfo}>
                      <span className={styles.actorName}>{actor.name}</span>
                      <span className={styles.characterName}>{actor.character}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className={styles.emptyMsg}>Information sur le casting non disponible</p>
            )}
          </section>

          {/* Synopsis */}
          <section className={styles.section}>
            <h3>Synopsis</h3>
            <p className={styles.overview}>{details.overview || "Aucun synopsis disponible."}</p>
          </section>

          {/* Where to watch */}
          <section className={styles.section}>
            <h3>Plateformes officielles</h3>
            <div className={styles.providersGrid}>
              {providers.length > 0 ? (
                providers.map((p: any) => (
                  <div key={p.provider_id} className={styles.providerItem}>
                    <img
                      src={`https://image.tmdb.org/t/p/original${p.logo_path}`}
                      alt={p.provider_name}
                      title={p.provider_name}
                    />
                  </div>
                ))
              ) : (
                <p className={styles.emptyMsg}>Non disponible</p>
              )}
            </div>
          </section>

          {/* External links */}
          <section className={styles.section}>
            <h3>Autres Streams</h3>
            <div className={styles.streamsContainer}>
              {Object.entries(STREAMS).map(([langKey, sites]) => (
                <div key={langKey} className={styles.langGroup}>
                  <div className={styles.langLabelWrapper}>
                    <h4 className={styles.langLabel}>{langKey}</h4>
                  </div>
                  <div className={styles.streamsGrid}>
                    {sites.map((stream: any) => (
                      <div className={styles.streamItem} key={stream.name}>
                        <a
                          href={stream.url + searchTerm}
                          className={styles.streamLink}
                          style={{ color: stream.color }}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {stream.name}
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}